import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, OnInit, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType } from '@ngx-formly/core';
import { ClipboardService } from 'ngx-clipboard';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { S_CRM_LAST_DIALED_NUMBER, S_CRM_LAST_START_OF_CALL } from '../../../@core/constants/settings-keys';
import { PhonePipe } from '../../../@core/pipes';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { NotificationService } from '../../../@core/services/notification/notification.service';
import { SettingsService } from '../../../@core/services/settings.service';

@Component({
  selector: 'app-phone-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './phone-input.component.html',
  providers: [PhonePipe],
  imports: [
    CommonModule,
    InputGroupModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule
  ]
})
export class PhoneInputComponent extends FieldType implements OnInit {

  phonePipe = inject(PhonePipe);
  destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  /** When used as a standalone component (not as Formly field type). */
  countryIso = input<string>(null);

  constructor(private cdRef: ChangeDetectorRef, private i18nService: I18nService,
    private settingsService: SettingsService,
    private clipboardService: ClipboardService, private notificationService: NotificationService) {
    super();
  }

  ngOnInit() {
    // Formly fields use modelOptions.updateOn: 'blur', so this only emits when the input loses focus.
    this.formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.applyPhoneFormat(value);
    });

    // Re-format when country changes (Formly expressions update props.countryIso from address country).
    this.form?.valueChanges?.pipe(
      takeUntilDestroyed(this.destroyRef),
      map(() => this.resolveCountryIso()),
      distinctUntilChanged(),
    ).subscribe(() => {
      // Allow Formly expressions to sync props.countryIso before reading it.
      queueMicrotask(() => {
        // While the user types, the control still holds the previous value; formatting happens on blur instead.
        if (this.isInputFocused()) {
          return;
        }

        this.applyPhoneFormat(this.formControl.value);
      });
    });
  }

  private resolveCountryIso(): string | null {
    return this.countryIso() ?? this.props?.countryIso ?? null;
  }

  private isInputFocused(): boolean {
    const activeElement = document.activeElement;

    return !!activeElement && this.elementRef.nativeElement.contains(activeElement);
  }

  private applyPhoneFormat(value: unknown): void {
    if (value === undefined || value === null || value === '') {
      return;
    }

    const countryIso = this.resolveCountryIso();
    const transformedValue = countryIso
      ? this.phonePipe.transform(value as string | number, countryIso)
      : this.phonePipe.transform(value as string | number);

    if (transformedValue !== value) {
      // emitEvent must stay true: Formly syncs the model from the control's valueChanges.
      this.formControl.patchValue(transformedValue, { emitEvent: true });
      this.cdRef.markForCheck();
    }
  }

  copyToClipboard(text: string) {
    this.clipboardService.copyFromContent(text);
    this.notificationService.success(_("common.copied-to-clipboard"), { entity: "company" });
  }

  startCall(phoneNumber: string) {
    window.location.href = `tel:${phoneNumber}`;

    this.settingsService.setSetting(S_CRM_LAST_START_OF_CALL, new Date());
    this.settingsService.setSetting(S_CRM_LAST_DIALED_NUMBER, phoneNumber);
  }

  getDataTestId(): string {
    if (!this.field?.props?.attributes || this.field.props.attributes['data-testid'] === undefined) {
      return "formly-phone";
    }
    else {
      let dataTestId = this.field?.props?.attributes['data-testid'] as string;
      return dataTestId;
    }
  }
}
